import { useEffect, useRef, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { OfflineProvider, OfflineRuntime } from '@floorfile/offline'
import { invalidateDerivedViews } from './derived-views'
import { useAuth } from './auth'
import { apiUrl } from './api-url'

/**
 * The app's one offline runtime (OFFLINE_&_MOBILE_RESILIENCE.md §3, D24).
 * Module-level singleton so the query-cache persister can share its
 * IndexedDB before React mounts; the fetcher late-binds to the live
 * authFetch — a drain's 401 rides authFetch's refresh-and-retry, which IS
 * the sync-time re-auth (§9): the httpOnly refresh cookie authorizes the
 * replay, and a rejected refresh leaves every queued op in place.
 */

type Fetcher = (path: string, init?: RequestInit) => Promise<Response>

let currentAuthFetch: Fetcher | undefined
let onOpAppliedHook: (() => void) | undefined
let onDirectAppliedHook: (() => void) | undefined

export const offlineRuntime = new OfflineRuntime({
  fetcher: (path, init) => (currentAuthFetch ?? fetch)(path, init),
  onOpApplied: () => onOpAppliedHook?.(),
  onDirectApplied: () => onDirectAppliedHook?.(),
  // Deployed Pages serve the SPA from a different origin than the API, and
  // the SPA fallback answers ANY same-origin path (including /api/*) with a
  // 200 HTML shell — so the heartbeat must target the configured API origin
  // and require a JSON answer, or reachability would never report offline.
  heartbeat: async () => {
    try {
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 4_000)
      const res = await fetch(apiUrl('/api/v1/health'), {
        signal: controller.signal,
        cache: 'no-store',
      })
      clearTimeout(timeout)
      return res.ok && (res.headers.get('content-type') ?? '').includes('application/json')
    } catch {
      return false
    }
  },
})

/** Which user's TanStack cache snapshot is on this device (shared tablets,
 * §9): a different user signing in must never see the prior user's reads. */
const CACHE_OWNER_KEY = 'floorfile.offline.cache-owner'

export function OfflineBridge({ children }: { children: ReactNode }) {
  const { authFetch, user, companyId, status } = useAuth()
  const queryClient = useQueryClient()
  const fetchRef = useRef(authFetch)
  fetchRef.current = authFetch
  useEffect(() => {
    currentAuthFetch = (path, init) => fetchRef.current(path, init)
  }, [])

  useEffect(() => {
    if (status === 'authenticated' && user && companyId) {
      const previousOwner = localStorage.getItem(CACHE_OWNER_KEY)
      if (previousOwner && previousOwner !== user.id) {
        // Per-user isolation (§9): drop the prior user's cached reads.
        // Their OUTBOX/drafts stay in IndexedDB tied to their id.
        queryClient.clear()
        void offlineRuntime.db.kv.clear()
      }
      localStorage.setItem(CACHE_OWNER_KEY, user.id)
      offlineRuntime.setUser({ userId: user.id, companyId })
    } else if (status === 'anonymous') {
      offlineRuntime.setUser(undefined)
    }
  }, [status, user, companyId, queryClient])

  // Applied ops changed server state (real numbers, computed fields) —
  // refetch whatever the UI currently shows (doc §5 reconciliation).
  //
  // Audit RUN-17: this fired an UNFILTERED `invalidateQueries()` once per
  // applied operation, from inside the sync engine's per-op loop. Reconnecting
  // a tablet that had been offline for a shift with N queued operations meant
  // N full-cache invalidations back to back, each refetching every mounted
  // query — a self-inflicted request storm at exactly the moment the device
  // has just proven the network is fragile, and directly against the standing
  // resource budget.
  //
  // Coalesced to ONE invalidation per drain rather than filtered by module:
  // an applied op can change computed state well outside its own module (a
  // check completion moves dashboard counts, a stock movement moves reorder
  // suggestions), so a prefix filter would trade a storm for staleness. The
  // microtask/timeout coalesce keeps the "refetch what the UI shows" contract
  // intact while making its cost O(1) per drain instead of O(N) per op.
  useEffect(() => {
    let pending: ReturnType<typeof setTimeout> | undefined
    onOpAppliedHook = () => {
      if (pending !== undefined) return
      pending = setTimeout(() => {
        pending = undefined
        void queryClient.invalidateQueries()
      }, 0)
    }
    // FF-AUDIT-011: an ONLINE write through `useOfflineMutation` bypasses the
    // react-query mutation cache (where every other write invalidates the
    // cross-module summaries), so it reports here instead — same list.
    onDirectAppliedHook = () => invalidateDerivedViews(queryClient)
    return () => {
      if (pending !== undefined) clearTimeout(pending)
      onOpAppliedHook = undefined
      onDirectAppliedHook = undefined
    }
  }, [queryClient])

  // FF-AUDIT-20260914-210459-096: when the reachability monitor says the
  // link is back, every mounted query refetches once — the check page that
  // errored in the dead zone recovers without a reload. `navigator.onLine`
  // is not the signal here (the probe is); one coalesced invalidation per
  // offline→online transition, never per probe.
  useEffect(() => {
    let previous = offlineRuntime.state
    return offlineRuntime.subscribe(() => {
      const next = offlineRuntime.state
      if (next === 'online' && previous !== 'online') {
        void queryClient.invalidateQueries({ type: 'active' })
      }
      previous = next
    })
  }, [queryClient])

  return <OfflineProvider runtime={offlineRuntime}>{children}</OfflineProvider>
}
